import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { parsePermissionsRouteState } from "permissions/_views/tables/_utilities/permissions-route-state";
import { Link as RouterLink, useLocation } from "react-router";

const PageTitleBreadcrumb = styled("span")(({ theme }) => ({
  color: "gray",
  display: "inline-flex",
  marginRight: theme.spacing(1),
  whiteSpace: "nowrap",
}));

type TitleSegment = {
  label: string;
  to?: string;
};

export function getPermissionsPageTitleSegments(search: string, userName = "User"): TitleSegment[] {
  const params = new URLSearchParams(search);
  const state = parsePermissionsRouteState(params);
  const home = "/permissions";

  if (state.granteeUserId) {
    const userRoute = home + "?granteeUserId=" + state.granteeUserId.value;
    const segments: TitleSegment[] = [{ label: "Permissions", to: home }, { label: userName }];

    if (params.get("action") === "manage-general") {
      segments[1] = { label: userName, to: userRoute };
      segments.push({ label: "General" });
    } else if (state.view === "details") {
      segments[1] = { label: userName, to: userRoute };
      segments.push({ label: "Specific" });
    }

    return segments;
  }

  if (params.get("action") === "manage-templates") {
    return [{ label: "Permissions", to: home }, { label: "Templates" }];
  }

  if (params.get("action") === "create-template") {
    return [
      { label: "Permissions", to: home },
      { label: "Templates", to: home + "?action=manage-templates" },
      { label: "Create" },
    ];
  }

  if (state.view === "details") {
    return [{ label: "Permissions", to: home }, { label: "Browse All" }];
  }

  return [{ label: "Permissions" }];
}

export default function PermissionsPageTitle() {
  const location = useLocation();
  const state = parsePermissionsRouteState(location.search);
  const individualQuery = useGetIndividualsByUserIdsQuery(
    state.granteeUserId ? { userIds: new Set([state.granteeUserId.value]) } : skipToken,
  );
  const individual = Individual.fromJSONArray(individualQuery.data, { allowEmpty: true })?.[0];
  const segments = getPermissionsPageTitleSegments(location.search, individual?.fullName || "User");

  return (
    <Typography component="h1" sx={(theme) => ({ paddingLeft: theme.spacing(1) })} variant="h4">
      {segments.map((segment, index) => {
        const isCurrent = index === segments.length - 1;

        if (isCurrent) return segment.label;

        return (
          <PageTitleBreadcrumb key={segment.label}>
            <Tooltip placement="bottom" title={"Back to " + segment.label}>
              <Link color="inherit" component={RouterLink} to={segment.to!} underline="hover">
                {segment.label}
              </Link>
            </Tooltip>
            &nbsp;&gt;
          </PageTitleBreadcrumb>
        );
      })}
    </Typography>
  );
}